import { component } from 'picoapp';
import { clear } from '~/js/utils/node';
import { buildProductItems } from '~/js/utils/product-item';
import Collection from '~/js/lib/collection';
import initFilterGroupDropdown from '~/js/lib/filter-group';
import filterDrawer from '~/js/lib/filter-drawer';
import closeIcon from '~/js/lib/closeIcon';
import {
  handleCollectionBanners,
  amountGroups,
  groupAlphabetically,
  initGroupSearch,
  setupQuickBuyPopups,
  updateProductCount,
  updateStoreHitsCount,
  selectedStores,
} from '~/js/lib/collection-filter-helpers';

const filledCheckSVG = '<svg class="filled opacity-0" id="Layer_1" style="enable-background:new 0 0 30 30;" version="1.1" viewBox="0 0 30 30" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><path d="M24,4H6C4.895,4,4,4.895,4,6v18c0,1.105,0.895,2,2,2h18c1.105,0,2-0.895,2-2V6C26,4.895,25.104,4,24,4z M21.707,11.707  l-7.56,7.56c-0.188,0.188-0.442,0.293-0.707,0.293s-0.52-0.105-0.707-0.293l-3.453-3.453c-0.391-0.391-0.391-1.023,0-1.414  s1.023-0.391,1.414,0l2.746,2.746l6.853-6.853c0.391-0.391,1.023-0.391,1.414,0S22.098,11.316,21.707,11.707z"/></svg>';
const emptyCheckSVG = '<svg class="empty" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"viewBox="0 0 30 30" style="enable-background:new 0 0 30 30;" xml:space="preserve"><g><path d="M24,5.5c0.3,0,0.5,0.2,0.5,0.5v18c0,0.3-0.2,0.5-0.5,0.5H6c-0.3,0-0.5-0.2-0.5-0.5V6c0-0.3,0.2-0.5,0.5-0.5H24M24,4H6C4.9,4,4,4.9,4,6v18c0,1.1,0.9,2,2,2h18c1.1,0,2-0.9,2-2V6C26,4.9,25.1,4,24,4L24,4z"/></g></svg>';
const checkSvg = `<div class="check-wrapper">${emptyCheckSVG}${filledCheckSVG}</div>`;

const renderFilterItem = (val, className, isActive, type) => `
  <div title="${val.value}" class="${className} ${val.hits < 1 ? 'disabled' : ''} ${isActive ? 'is-active' : ''}" data-type="${type}" data-value="${val.value}" data-uppercased-value="${val.value.toUpperCase()}">
    <div class="${className}-inner">
  ${checkSvg}
  <span class="${className}-inner-val">${val.value}</span>
<span class="js-valueCount">${val.hits}</span></div>
  </div>
`;

export default component(async (node, ctx) => {
  const { handle, collectionId, defaultSort } = JSON.parse(node.dataset.config);

  const filterEl = node.querySelector('[data-filter-groups]');
  const filterGroups = [...node.querySelectorAll('[data-filter-group]')];
  const filterDrawerElement = node.querySelector('[data-filter-drawer]');
  const placeholderFilter = node.querySelector('[data-placeholder-groups]');
  const mobileFilterBtn = node.querySelector('[data-drawer-trigger="filter"]');

  const paginationElement = node.querySelector('[data-collection-status]');
  const paginationProgressElement = paginationElement?.querySelector('[data-progress]');
  const paginationTextElement = paginationElement?.querySelector('[data-progress-translation]');
  const loadMoreButton = paginationElement?.querySelector('[data-load-more]');

  const sortElements = [...node.querySelectorAll('[data-sort]')];
  const toggleStoreFilter = document.querySelector('input[data-toggle-stores]');

  const totalFiltersCount = node.querySelector('[data-total-filters]');

  const output = node.querySelector('[data-output]');

  const limit = 24; // products per page
  const location = 'Collection page'; // relewise data

  const searchParams = new URLSearchParams(window.location.search);
  let page = Number(searchParams.get('p') ?? 1);

  let viewed = 0; // count in current view
  let facets = []; // relewise filters
  let hits = 0; // total hits (including pagination)

  const CollectionModule = new Collection();
  const queries = []; // active filters

  const filterKeys = filterGroups
    .map((group) => group.dataset.group)
    .filter((i) => i);
  const whitelistedFilterKeys = [...new Set(filterKeys), `${window.Shopify.shop}_HasInventory`, 'IsOnSale'];

  /**
   * grabs and stores initial queries/filters from URL
   * @returns {void}
   */
  const setInitialQueries = () => {
    const entries = new URLSearchParams(window.location.search).entries();

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of entries) {
      if (whitelistedFilterKeys.includes(key.trim())) {
        queries.push({ key, value });
      }
    }

    const selected = selectedStores();

    if (selected.length) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const urlStores = urlSearchParams.getAll(`${window.Shopify.shop}_HasInventory`);

      const newUrlSearchParams = new URLSearchParams();

      selected.forEach((store) => {
        if (!urlStores.includes(store)) {
          queries.push({
            key: `${window.Shopify.shop}_HasInventory`, value: store,
          });
        }
      });

      queries.forEach((query) => {
        newUrlSearchParams.append(query.key, query.value);
      });

      window.history.replaceState(null, null, `?${newUrlSearchParams.toString()}`);

      toggleStoreFilter.checked = selected.length;
    }
  };

  /**
   * updates queries/filters from URL
   */
  const readQueries = () => {
    const entries = new URLSearchParams(window.location.search).entries();

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of entries) {
      if (whitelistedFilterKeys.includes(key.trim())) {
        const existingQuery = queries.find((query) => query.key === key && query.value === value);
        if (!existingQuery) {
          queries.push({ key, value });
        }
      }
    }
  };

  const writeQueries = ({ clearBeforeRender }) => {
    const scopedSearchParams = new URLSearchParams();

    queries.forEach((query) => {
      if (query.key === 'p' || query.key === 'SalesPrice') {
        scopedSearchParams.set(query.key, query.value);
      } else {
        scopedSearchParams.append(query.key, query.value);
      }
    });

    window.history.replaceState(null, null, `?${scopedSearchParams.toString()}`);

    node.dispatchEvent(new CustomEvent('queries:updated', {
      detail: { clearBeforeRender },
    }));
  };

  /**
   * This is needed since some facet vals are returned
   * as { hits: number, selected: boolean, value: { id: string }
   * and others as
   * { hits: number, selected: boolean, value: string }
   * :)
   * @param {[{ hits: number, selected: boolean, value: { id: string }}]} vals - relewise facet response
   * @returns {[{ hits: number, selected: boolean, value: string }]}
   */
  const getConvertedVals = (vals) => vals.map((valObject) => {
    if (valObject.value && typeof valObject.value === 'object' && 'id' in valObject.value) {
      return {
        hits: valObject.hits, selected: valObject.selected, value: valObject.value.id,
      };
    }
    return valObject;
  });

  /**
   * @param {} vals - relewise facet response
   * @returns vals - sorted alphabetically
   */
  const sortFilterValues = (vals) => {
    const danishSortAlphabetically = (a, b) => a.localeCompare(b);

    const convertedVals = getConvertedVals(vals);
    return convertedVals.sort((a, b) => danishSortAlphabetically(a.value, b.value));
  };

  /**
   * Adds event listeners to filter items to make them reactive
   * @returns {void}
   */
  const initFacets = () => {
    filterGroups.forEach((group) => {
      const { group: paramKey, mobile: isMobile } = group.dataset;
      let container = group;

      if (isMobile && paramKey !== 'SalesPrice') {
        container = group.parentNode.querySelector(`[data-drawer-type="${paramKey}"]`);
      }

      if (paramKey === 'sort_by') return;

      const items = [...container.querySelectorAll('.filter-pill')];

      amountGroups(container);

      items.forEach((item) => {
        const { value: paramValue } = item.dataset;

        item.addEventListener('click', () => {
          item.classList.toggle('is-active');

          if (item.hasAttribute('data-sale-filter')) {
            if (item.classList.contains('is-active')) {
              if (!queries.find((q) => q.key === 'IsOnSale')) {
                queries.push({ key: 'IsOnSale', value: 'true' });
              }
            } else {
              Array.from(document.querySelectorAll('[data-sale-filter]')).filter((filter) => filter !== item).forEach((filter) => {
                filter.classList.remove('is-active');
              });

              queries.splice(queries.findIndex((q) => q.key === 'IsOnSale'), 1);
            }
          } else if (item.classList.contains('is-active')) {
            queries.push({ key: paramKey, value: paramValue });
          } else {
            queries.splice(queries.findIndex((q) => q.key === paramKey && q.value === paramValue), 1);
          }

          writeQueries({ clearBeforeRender: true });
          amountGroups(container);
        });
      });
    });

    document.addEventListener('store:selected', () => {
      const selected = selectedStores();

      toggleStoreFilter.checked = selected.length;
      queries.length = 0;

      const entries = new URLSearchParams(window.location.search).entries();

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of entries) {
        if (key !== `${window.Shopify.shop}_HasInventory`) {
          queries.push({ key, value });
        }
      }

      selected.forEach((store) => {
        queries.push({
          key: `${window.Shopify.shop}_HasInventory`, value: store,
        });
      });

      writeQueries({ clearBeforeRender: true });
    });

    if (toggleStoreFilter) {
      toggleStoreFilter.addEventListener('change', () => {
        const selected = selectedStores();

        if (!selected.length) {
          toggleStoreFilter.parentElement.nextElementSibling.click();
          return;
        }

        if (toggleStoreFilter.checked) {
          selected.forEach((store) => {
            queries.push({
              key: `${window.Shopify.shop}_HasInventory`, value: store,
            });
          });
          toggleStoreFilter.parentElement.parentElement.parentElement.classList.add('border-black');
          toggleStoreFilter.parentElement.parentElement.parentElement.classList.remove('border-warm-gray-200');
        } else {
          selected.forEach((storeQuery) => {
            queries.splice(queries.findIndex((q) => q.key === `${window.Shopify.shop}_HasInventory` && q.value === storeQuery), 1);
          });
          toggleStoreFilter.parentElement.parentElement.parentElement.classList.remove('border-black');
          toggleStoreFilter.parentElement.parentElement.parentElement.classList.add('border-warm-gray-200');
        }

        writeQueries({ clearBeforeRender: true });
      });
    }
  };

  /**
   * Renders a list of active filters
   * paramKey is used in mobile view
   * @returns {void}
   */
  const setActiveFilters = () => {
    const filterListElements = [...node.querySelectorAll('[data-active-filters-list]')];

    filterListElements.forEach((filterListElement) => {
      filterListElement.innerHTML = '';

      const { activeFiltersList: paramKey } = filterListElement.dataset;
      let activeFilters = queries;

      if (paramKey) {
        activeFilters = queries.filter((query) => query.key === paramKey);
      }

      activeFilters.forEach((query) => {
        if (query.key === 'IsOnSale') {
          Array.from(document.querySelectorAll('[data-sale-filter]')).forEach((filter) => {
            if (!filter.classList.contains('is-active')) {
              filter.classList.add('is-active');
            }
          });
        }

        if (query.key === 'p' || query.key === 'SalesPrice' || query.key === 'sort_by' || query.key === `${window.Shopify.shop}_HasInventory` || query.key === 'IsOnSale') {
          return;
        }

        const listItem = document.createElement('li');

        if (paramKey) {
          listItem.classList.add('mr-8');
          listItem.innerHTML = query.value;
        } else {
          listItem.classList.add('button', 'button--active-filter');
          listItem.innerHTML = `${query.value}${closeIcon('ml-4')}`;
        }

        filterListElement.appendChild(listItem);

        if (!paramKey) {
          listItem.addEventListener('click', () => {
            const matchingButton = node.querySelector(`[data-value="${query.value}"].is-active`);
            if (matchingButton) matchingButton.click();
          });
        }
      });

      if (paramKey) filterListElement.hidden = !activeFilters.length;
    });
  };

  const enablePriceFilter = async ({ group, facetItem, isMobile }) => {
    let existingPriceQuery = queries.find((query) => query.key === 'SalesPrice');

    const priceSlider = group.querySelector('.js-price-handle-slider');
    const manualMinInput = group.querySelector('[name="manual-min-price"]');
    const manualMaxInput = group.querySelector('[name="manual-max-price"]');

    const rangeStatusMinEl = group.querySelector('[data-price-range-min]');
    const rangeStatusMaxEl = group.querySelector('[data-price-range-max]');

    const { lowerBoundInclusive, upperBoundInclusive } = facetItem?.available?.value || {};
    const min = Math.floor(lowerBoundInclusive);
    const max = Math.ceil(upperBoundInclusive);

    let [startMin, startMax] = [min, max];
    if (existingPriceQuery) {
      [startMin, startMax] = existingPriceQuery.value.split('-');
    }

    const updateStatusEls = (newMin, newMax) => {
      const formattedMin = Math.trunc(newMin * 100);
      const formattedMax = Math.trunc(newMax * 100);

      if (rangeStatusMinEl && newMin) {
        rangeStatusMinEl.textContent = Shopify.formatMoney(formattedMin, theme.moneyFormat);
      }

      if (rangeStatusMaxEl && newMax) {
        rangeStatusMaxEl.textContent = Shopify.formatMoney(formattedMax, theme.moneyFormat);
      }

      if (manualMinInput && newMin) {
        manualMinInput.value = Shopify.formatMoney(formattedMin, theme.moneyFormat);
      }

      if (manualMaxInput && newMax) {
        manualMaxInput.value = Shopify.formatMoney(formattedMax, theme.moneyFormat);
      }
    };

    const updatePrices = (range) => {
      existingPriceQuery = queries.find((query) => query.key === 'SalesPrice');
      let [min, max] = range; // eslint-disable-line no-shadow

      if (!min) {
        min = existingPriceQuery ? existingPriceQuery.value.split('-')[0] : startMin;
      }
      if (!max) {
        max = existingPriceQuery ? existingPriceQuery.value.split('-')[1] : startMax;
      }

      if (existingPriceQuery) {
        existingPriceQuery.value = `${min}-${max}`;
      } else {
        queries.push({ key: 'SalesPrice', value: `${min}-${max}` });
      }

      writeQueries({ clearBeforeRender: true });
    };

    if (priceSlider) {
      const noUiSlider = (await import(
        /* webpackChunkName: "no-ui-slider" */
        'modules/no-ui-slider'
      )).default;

      noUiSlider.create(priceSlider, {
        start: [startMin, startMax],
        step: 1,
        connect: true,
        range: {
          min: [min], max: [max],
        },
      });

      ctx.on('price:slider:reset', () => {
        priceSlider.noUiSlider.set([startMin, startMax]);
      });
      priceSlider.noUiSlider.on('change', updatePrices);
      priceSlider.noUiSlider.on('update', (range) => updateStatusEls(range[0], range[1]));
    }

    if (manualMinInput) {
      manualMinInput.value = Shopify.formatMoney(startMin * 100, theme.moneyFormat);

      manualMinInput.addEventListener('change', () => {
        const newMinVal = parseInt(manualMinInput.value, 10);

        if (priceSlider) {
          priceSlider.noUiSlider.set([newMinVal, null]);
        }

        updatePrices([newMinVal, null]);
      });
    }

    if (manualMaxInput) {
      manualMaxInput.value = Shopify.formatMoney(startMax * 100, theme.moneyFormat);

      manualMaxInput.addEventListener('change', () => {
        const newMaxVal = parseInt(manualMaxInput.value, 10);

        if (priceSlider) {
          priceSlider.noUiSlider.set([null, newMaxVal]);
        }

        updatePrices([null, newMaxVal]);
      });
    }

    if (!isMobile) {
      initFilterGroupDropdown(group);
    }
  };

  /**
   * Builds and renders filter groups based off which...
   * ...possible filters Relewise returned
   * @returns {void}
   */
  const setFacetGroups = () => {
    filterGroups.forEach((group) => {
      const { group: field, mobile: isMobile } = group.dataset;
      const groupElement = group.parentNode.querySelector(`[data-filter-container="${field}"]`);
      const facetItem = facets?.items?.find((facet) => (facet.key ? facet.key === field.replace('SHOP', window.Shopify.shop) : facet.field === field));

      let searchField = null;

      if (isMobile) {
        searchField = group.nextElementSibling?.querySelector('[data-group-search]');
      } else {
        searchField = group.querySelector('[data-group-search]');
      }

      if (field === 'SalesPrice' || field === 'sort_by') {
        enablePriceFilter({ group, facetItem, isMobile });
        return;
      }

      let available = facetItem?.available;
      if (!available || !available.length) {
        group.hidden = true;
        return;
      }

      if (!groupElement) return;

      available = sortFilterValues(available);

      groupElement.classList.add('js-group', 'tw-pl-5');

      if (facetItem.key === `${window.Shopify.shop}_Size`) {
        const tmpObj = {};

        available.forEach((val) => {
          const type = val.value.split(':')[0];
          if (!tmpObj[type]) tmpObj[type] = [];
          tmpObj[type].push(val);
        });

        groupElement.innerHTML = Object.keys(tmpObj).map((key) => tmpObj[key].sort((a, b) => a.value.localeCompare(b.value, ['da', 'sv'], { numeric: true })).map((val, idx) => {
          const isActive = queries.find((query) => query.key === field && query.value === val.value);
          return `
${idx === 0 ? `<div class="font-demi font-bold tw-mb-2.5 block w-full">${val.value.split(':')[0]}</div>` : ''}
          <div class="tw-mb-1 tw-mr-1 filter-pill filter-pill--button ${val.hits === 0 ? 'disabled' : ''} ${isActive ? 'is-active' : ''}" data-type="select" data-value="${val.value}" data-uppercased-value="${val.value.toUpperCase()}">
    ${val.value.split(':')[1]}
  </div>
  
${idx === tmpObj[key].length - 1 ? '<div class="h-px bg-basics-medium-grey tw-mt-3 tw-mb-4"></div>' : ''}
`;
        })
          .join(''))
          .join('');
      } else {
        groupElement.innerHTML = available
          .map((val) => {
            const isActive = queries.find((query) => query.key === field && query.value === val.value);
            return renderFilterItem(val, 'filter-pill', isActive, 'select');
          })
          .join('');
      }

      if (groupElement.getAttribute('data-group-alphabetically') !== null) {
        groupAlphabetically(groupElement);
      }

      if (searchField) {
        initGroupSearch(searchField, groupElement);
      }

      if (!isMobile) {
        initFilterGroupDropdown(group);
      }
    });

    updateStoreHitsCount(facets);

    initFacets();
    filterDrawer(filterDrawerElement);

    // update loading state
    filterEl.hidden = false;
    placeholderFilter.hidden = true;
    mobileFilterBtn.classList.remove('pointer-events-none', 'animate-pulse', 'bg-gray-400');
  };

  const renderProducts = (results) => {
    output.insertAdjacentHTML('beforeend', buildProductItems(results, 'card', 'relewise', handle));

    updateProductCount(node, viewed, hits);
    handleCollectionBanners(node, output, viewed);
    setupQuickBuyPopups(node, results);

    document.dispatchEvent(new Event('app:remount'));
  };

  /**
   * updates the pagination progress and button
   * @returns {void}
   */
  const setPagination = () => {
    if (!paginationElement) return;

    const translate = (curr) => window.theme.strings.collectionStatus
      .replace('{{ count }}', curr)
      .replace('{{ max }}', hits);

    const update = () => {
      const currProgress = (viewed / hits) * 100;

      paginationProgressElement.style.width = `${currProgress}%`;
      paginationTextElement.innerHTML = translate(viewed);

      loadMoreButton.hidden = viewed >= hits;
    };

    update();
  };

  /**
   * Updates page and queries state and writes URL queries...
   * ...which triggers queries:updated
   */
  const loadMoreProducts = () => {
    page++;
    queries.push({ key: 'p', value: page });

    writeQueries({ clearBeforeRender: false });
  };

  /**
   * Fetches products and filters from Relewise
   * Updates state variables and handles clearing if applicable
   * @param {Boolean} clearBeforeRender - handles render fetch variables as limit and page ...also possibly clears the container
   * @returns Promise<Relewise response>
   */
  const fetchProducts = async ({ clearBeforeRender }) => {
    const response = await CollectionModule.getProductsAndFilters({
      collectionId,
      limit: clearBeforeRender ? limit * page : limit,
      location,
      page: clearBeforeRender ? 1 : page,
      facetGroups: filterGroups.map((group) => group.dataset.group),
      queries,
      defaultSort: defaultSort || 'relevance',
    });

    if (clearBeforeRender) {
      viewed = response.results.length;
      clear(output);
    } else {
      viewed += response.results.length;
    }

    facets = response.facets;
    hits = response.hits;

    const promotedResults = response.retailMedia?.placements?.TOP?.results;

    if (promotedResults?.length > 0 && (page === 1 || clearBeforeRender)) {
      response.results.unshift(...promotedResults.map((result) => ({
        ...result.promotedProduct.result, promoted: true,
      })));
    }

    return response;
  };

  const updateTotalFiltersCount = () => {
    const total = queries.filter((query) => query.key !== 'sort_by' && query.key !== 'p').length;
    totalFiltersCount.innerHTML = total;

    totalFiltersCount.parentNode.hidden = total === 0;
  };

  /**
   * Updates counter next to a filter item
   */
  const updateFilterItemCounts = () => {
    filterGroups.forEach((group) => {
      const { group: field } = group.dataset;
      const groupElement = group.parentNode.querySelector(`[data-filter-container="${field}"]`);

      if (!groupElement) return;

      const filterPills = groupElement.querySelectorAll('.filter-pill');
      filterPills.forEach((pill) => {
        const { value } = pill.dataset;
        const valueCountElement = pill.querySelector('.js-valueCount');
        const facetItem = facets?.items?.find((facet) => (facet.key ? facet.key === field.replace('SHOP', window.Shopify.shop) : facet.field === field.replace('SHOP', window.Shopify.shop)));
        const filterItem = facetItem.available.find((val) => {
          if (typeof val.value === 'object' && val.value !== null) {
            return val.value.id === value;
          }
          return val.value === value;
        });

        if (filterItem) {
          if (valueCountElement) {
            valueCountElement.textContent = filterItem.hits;
          }

          if (filterItem.hits < 1) {
            pill.classList.add('disabled');
          } else {
            pill.classList.remove('disabled');
          }
        }
      });
    });

    updateStoreHitsCount(facets);
  };

  const resetFilters = () => {
    // remove all queries except for p and sort_by
    queries.splice(0, queries.length, ...queries.filter((query) => query.key === 'p' || query.key === 'sort_by'));

    filterGroups.forEach((group) => {
      const activeFilterPills = [...node.querySelectorAll('.is-active')];
      const { group: paramKey, mobile: isMobile } = group.dataset;
      let container = group;

      if (isMobile && paramKey !== 'SalesPrice') {
        container = group.parentNode.querySelector(`[data-drawer-type="${paramKey}"]`);
      }

      activeFilterPills.forEach((pill) => {
        pill.classList.remove('is-active');
      });

      amountGroups(container);
    });

    writeQueries({ clearBeforeRender: true });
    ctx.emit('price:slider:reset');
  };

  const toggleGlobalResetButton = () => {
    const resetButtons = node.querySelectorAll('[data-action="reset"]');
    const filterQueries = queries.filter((query) => query.key !== 'p' && query.key !== 'sort_by');

    resetButtons.forEach((resetButton) => {
      resetButton.hidden = !filterQueries.length;
      resetButton.addEventListener('click', resetFilters);
    });
  };

  const setupSortOptions = () => {
    const masterElement = sortElements.find((sortElement) => sortElement.dataset.sort === 'master');
    const selectElement = sortElements.find((sortElement) => sortElement !== masterElement);
    const sortButtons = [...masterElement.parentNode.querySelectorAll('[data-btn-sort]')];
    let existingSortQuery = queries.find((query) => query.key === 'sort_by') ?? { value: defaultSort };
    const activeSortingElement = node.querySelector('[data-sorting-active]');

    let initialSortButton = sortButtons.find((sortButton) => sortButton.dataset.btnSort === defaultSort);
    let initialSortName = initialSortButton?.textContent ?? 'Relevance';

    /**
     * Build sort options for mobile
     */
    sortButtons.forEach((sortButton) => {
      const optionElement = document.createElement('option');
      optionElement.value = sortButton.dataset.btnSort;
      optionElement.textContent = sortButton.textContent;

      if (sortButton.classList.contains('is-active')) {
        optionElement.selected = true;
      }

      selectElement.appendChild(optionElement);
    });

    /**
     * @param {string} value sort value
     */
    const handleSortChange = (value) => {
      existingSortQuery = queries.find((query) => query.key === 'sort_by');

      if (existingSortQuery) {
        existingSortQuery.value = value;
      } else {
        queries.push({ key: 'sort_by', value });
      }

      writeQueries({ clearBeforeRender: true });
    };

    selectElement.addEventListener('change', () => {
      handleSortChange(selectElement.value);
    });

    sortButtons.forEach((sortButton) => {
      sortButton.addEventListener('click', () => {
        sortButtons.forEach((btn) => {
          btn.classList.remove('is-active');
        });

        sortButton.classList.add('is-active');
        activeSortingElement.textContent = sortButton.textContent;
        handleSortChange(sortButton.dataset.btnSort);
      });
    });

    /**
     * Set initial state
     */
    if (existingSortQuery) {
      initialSortButton = sortButtons.find((sortButton) => sortButton.dataset.btnSort === existingSortQuery.value);
      initialSortName = initialSortButton?.textContent ?? 'Relevance';

      selectElement.value = existingSortQuery.value;
      activeSortingElement.textContent = initialSortName;
    }

    sortButtons.forEach((sortButton) => {
      const match = existingSortQuery?.value ?? 'relevance';
      if (sortButton.dataset.btnSort === match) {
        sortButton.classList.add('is-active');
      }
    });
  };

  const useFallback = () => {
    const fallbackOutput = node.querySelector('[data-fallback]');
    placeholderFilter.hidden = true;
    paginationElement.hidden = true;
    filterEl.hidden = true;
    output.hidden = true;

    fallbackOutput.hidden = false;
  };

  const addEventListeners = async () => {
    loadMoreButton.addEventListener('click', loadMoreProducts);

    /**
     * Triggered by writeQueries
     * - Which are called in loadMoreProducts and initFacets
     * ...updates the collection by filters or pagination
     */
    node.addEventListener('queries:updated', async (e) => {
      const { clearBeforeRender } = e.detail;

      try {
        const refetchedProducts = await fetchProducts({ clearBeforeRender });
        if (refetchedProducts.results.length === 0) {
          useFallback();
          return;
        }
        renderProducts(refetchedProducts.results);
        setPagination();
        updateTotalFiltersCount();
        updateFilterItemCounts();
        setActiveFilters();
        toggleGlobalResetButton();
      } catch (error) {
        useFallback();
        // eslint-disable-next-line no-console
        console.error(error.message);
      }
    });
  };

  /**
   * INITIALIZATION
   */

  setInitialQueries();
  readQueries();

  try {
    const fetchedProducts = await fetchProducts({ clearBeforeRender: true });
    if (fetchedProducts.results.length === 0) {
      useFallback();
      return;
    }
    setFacetGroups();
    clear(output);
    renderProducts(fetchedProducts.results);
    setPagination();
    setActiveFilters();
    toggleGlobalResetButton();
    setupSortOptions();
    addEventListeners();
  } catch (error) {
    useFallback();
    // eslint-disable-next-line no-console
    console.error(error);
  }
});
